/**
 * Adds _language / _market / _domain line item properties to EVERY add-to-cart, so the
 * order records which storefront the customer was shopping on. See
 * snippets/locale-line-properties.liquid for the why; the two main product forms carry
 * the same values as hidden inputs, and this file covers every other way an item gets
 * into a cart:
 *
 *   - fetch() to /cart/add(.js)   theme product forms, quick add, bundles, upsells
 *   - form POSTs to /cart/add      page-cart mode, FoxTheme.utils.postLink (see theme.js)
 *   - <a href="/cart/add?id=..">   Bloomreach product cards (they live in a shadow root)
 *
 * Existing values are never overwritten, so the Liquid-rendered inputs stay authoritative.
 */
(() => {
  let properties;
  try {
    properties = JSON.parse(document.getElementById('locale-line-properties-config').textContent);
  } catch (e) {
    return;
  }
  const keys = Object.keys(properties);
  window.CnxLocaleLine = { properties };

  const isCartAdd = (url) => {
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  };

  // ---- fetch -------------------------------------------------------------------------
  const withProperties = (body) => {
    if (body instanceof FormData) {
      const itemIndexes = [];
      for (const name of body.keys()) {
        const match = name.match(/^items\[(\d+)\]\[id\]$/);
        if (match) itemIndexes.push(match[1]);
      }
      const prefixes = itemIndexes.length ? itemIndexes.map((i) => `items[${i}][properties]`) : ['properties'];
      prefixes.forEach((prefix) =>
        keys.forEach((key) => {
          if (!body.has(`${prefix}[${key}]`)) body.append(`${prefix}[${key}]`, properties[key]);
        })
      );
      return body;
    }

    if (body instanceof URLSearchParams) {
      keys.forEach((key) => {
        if (!body.has(`properties[${key}]`)) body.append(`properties[${key}]`, properties[key]);
      });
      return body;
    }

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        if (json && typeof json === 'object') {
          const stamp = (target) => {
            target.properties = { ...properties, ...(target.properties || {}) };
          };
          if (Array.isArray(json.items)) json.items.forEach(stamp);
          else stamp(json);
          return JSON.stringify(json);
        }
      } catch (e) {
        // not JSON: treat as a urlencoded body
        const params = new URLSearchParams(body);
        keys.forEach((key) => {
          if (!params.has(`properties[${key}]`)) params.append(`properties[${key}]`, properties[key]);
        });
        return params.toString();
      }
    }
    return body;
  };

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : null;
      if (url && init && init.body && isCartAdd(url)) init = { ...init, body: withProperties(init.body) };
    } catch (e) {
      // never let stamping break an add-to-cart
    }
    return nativeFetch.call(this, input, init);
  };

  // ---- native form submits (page-cart mode and forms without the Liquid inputs) ------
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !isCartAdd(form.getAttribute('action') || '')) return;
      keys.forEach((key) => {
        if (form.querySelector(`[name="properties[${key}]"]`)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = properties[key];
        form.appendChild(input);
      });
    },
    true
  );

  // ---- add-to-cart links (Bloomreach cards, inside a shadow root) ---------------------
  document.addEventListener(
    'click',
    (event) => {
      const link = event.composedPath().find((el) => el instanceof HTMLAnchorElement && el.getAttribute('href'));
      if (!link || !isCartAdd(link.getAttribute('href'))) return;
      const url = new URL(link.getAttribute('href'), window.location.origin);
      keys.forEach((key) => {
        if (!url.searchParams.has(`properties[${key}]`)) url.searchParams.set(`properties[${key}]`, properties[key]);
      });
      link.setAttribute('href', url.pathname + url.search);
    },
    true
  );
})();
